"use client";

import { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { motion } from "framer-motion";

import "./ERPProcessSection.scss";

const ERPProcessSection = ({ process }) => {
  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  if (!process) return null;

  const {
    title = "",
    description = "",
    processData = [],
  } = process;

  const renderHighlightedTitle = (titleText = "") => {
    const parts = titleText.split(/(".*?")/g);

    return parts.map((part, index) => {
      if (part.startsWith('"') && part.endsWith('"')) {
        return (
          <span
            key={index}
            className="highlight-font"
          >
            {part.slice(1, -1)}
          </span>
        );
      }

      return part;
    });
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 576);
    };

    checkMobile();

    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    if (!processData.length) return undefined;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % processData.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [processData.length]);

  if (!processData.length) return null;

  return (
    <section
      ref={sectionRef}
      className="erp-process-section"
    >
      <Container>
        <div className="erp-process-sticky">
          <div className="erp-process-container">
            <div className="erp-process-left">
              <div className="erp-ring-system">
                <div className="ring ring-1" />
                <div className="ring ring-2" />
                <div className="ring ring-3" />
                <div className="ring ring-4" />
                <div className="ring ring-5" />
              </div>

              <div className="erp-center-circle">
                <h2 className="erp-center-title">
                  {renderHighlightedTitle(title)}
                </h2>

                <p className="erp-center-description">
                  {description}
                </p>
              </div>
            </div>

            <div className="erp-process-right">
              {processData.map((item, index) => {
                const isActive = activeIndex === index;

                return (
                  <motion.div
                    key={item.section || index}
                    className={`erp-process-item ${
                      isActive ? "active" : "inactive"
                    }`}
                    animate={
                      isMobile
                        ? {
                            opacity: 1,
                            filter: "blur(0px)",
                            scale: 1,
                          }
                        : {
                            opacity: isActive ? 1 : 0.3,
                            filter: isActive
                              ? "blur(0px)"
                              : "blur(1px)",
                            scale: isActive ? 1.05 : 0.93,
                          }
                    }
                    transition={{
                      duration: isMobile ? 0 : 1,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <div className="erp-item-dot-container">
                      <motion.div
                        className="erp-item-dot"
                        animate={
                          isMobile
                            ? {
                                scale: 1,
                                boxShadow:
                                  "0 0 0px rgba(255,95,87,0)",
                              }
                            : {
                                scale: isActive
                                  ? [1, 1.2, 1]
                                  : 0.7,
                                boxShadow: isActive
                                  ? [
                                      "0 0 0px rgba(255,95,87,0.2)",
                                      "0 0 24px rgba(255,95,87,0.5)",
                                      "0 0 0px rgba(255,95,87,0.2)",
                                    ]
                                  : "0 0 0px rgba(255,95,87,0)",
                              }
                        }
                        transition={{
                          duration: 2,
                          repeat:
                            !isMobile && isActive
                              ? Infinity
                              : 0,
                          ease: "easeInOut",
                        }}
                      />
                    </div>

                    <div className="erp-item-content">
                      <h3 className="erp-item-number">
                        {item.section}
                      </h3>

                      <div className="erp-item-text">
                        <h4 className="erp-item-title">
                          {item.title}
                        </h4>

                        <p className="erp-item-description">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ERPProcessSection;